import { booleanAttribute, ChangeDetectionStrategy, Component, inject, input, Signal, } from '@angular/core';
import { Router } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faList, faSortDown, faSortUp, faTh } from '@fortawesome/free-solid-svg-icons';
import { injectQueryParams } from 'ngxtension/inject-query-params';
import { ItemsViewService } from '../../../../@domain/items-view/items-view.service';
import { AuthorInfoSort, CategorySort, EventInfoSort, HistorySort, SortOrder, VideoInfoSort } from '../../../../@generated';
import { DropdownComponent } from './dropdown.component';

type Option = 'category' | 'author' | 'events' | 'video' | 'history';

@Component({
    selector: 'app-filters',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ DropdownComponent, FaIconComponent ],
    styles: `
        @use "variables" as *;

        :host {
            width: 100%;
            display: flex;
            justify-content: space-between;
        }

        .group {
            display: flex;
            gap: 1rem;
            align-items: center;
        }

        .sort-btn {
            height: 36px;
            width: 36px;
            background-color: $bg-light;
            position: relative;
            cursor: pointer;

            fa-icon {
                color: $grey-light;
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);

                &.active {
                    color: $black;
                }
            }
        }

        @media screen and (max-width: 600px) {
            .group {
                gap: 0.3rem;
            }

            .sort-btn {
                height: 30px;
                width: 30px;
                font-size: 0.8rem;
            }
        }
    `,
    template: `
        <div class="group">
            @if (type()) {
                <app-dropdown defaultName="Type" parameter="type" [items]="types"/>
            }
            @if (years(); as type) {
                <app-dropdown defaultName="Year" parameter="year"
                              [items]="type === 'event' ? yearsEvents : yearsVideos"/>
            }
            @if (language()) {
                <app-dropdown defaultName="Language" parameter="language" [items]="languageOptions"/>
            }
        </div>

        <div class="group">
            @if (sortType()) {
                <app-dropdown defaultName="Sort" parameter="sort" [items]="options[sortType()!]"
                              dropdownPosition="end"/>
            }

            @if (order()) {
                <button class="sort-btn" (click)="toggleOrder(sortOrder())">
                    @let isAscending = sortOrder() === SortOrder.Ascending;

                    <fa-icon [icon]="faSortUp" [class.active]="isAscending"/>
                    <fa-icon [icon]="faSortDown" [class.active]="!isAscending"/>
                </button>
            }

            @if (view()) {
                <button class="sort-btn" (click)="toggleView()">
                    <fa-icon [icon]="isGridView() ? faList : faTh" size="lg" class="active"/>
                </button>
            }
        </div>
    `
})
export class SortComponent {
    private viewService = inject(ItemsViewService);
    private router = inject(Router);

    public language = input(false, { transform: booleanAttribute });
    public type = input<'video' | 'event'>();
    public years = input<'video' | 'event'>();
    public order = input(false, { transform: booleanAttribute });
    public view = input(false, { transform: booleanAttribute });
    public sortOrder = injectQueryParams('order', { initialValue: SortOrder.Descending }) as Signal<SortOrder>;
    public sortType = input<Option>();
    public isGridView = this.viewService.isGridView;
    public SortOrder = SortOrder;
    public faSortUp = faSortUp;
    public faSortDown = faSortDown;
    public faList = faList;
    public faTh = faTh;

    options: Record<Option, any> = {
        category: [
            { name: 'Alphabet', value: CategorySort.Name },
            { name: 'Number of videos', value: CategorySort.TotalVideos },
        ],
        author: [
            { name: 'Alphabet', value: AuthorInfoSort.Name },
            { name: 'Views', value: AuthorInfoSort.TotalVideos },
        ],
        events: [
            { name: 'Alphabet', value: EventInfoSort.Title },
            { name: 'Date', value: EventInfoSort.Date },
        ],
        video: [
            { name: 'Alphabet', value: VideoInfoSort.Title },
            { name: 'Date', value: VideoInfoSort.Date },
            { name: 'Views', value: VideoInfoSort.TotalViews },
        ],
        history: [
            { name: 'Alphabet', value: HistorySort.Title },
            { name: 'Date', value: HistorySort.WatchDate },
            { name: 'Views', value: HistorySort.TotalViews },
        ],
    };

    yearsEvents = [
        { name: 'All years', value: '' },
        { name: '2001', value: '2001' },
        { name: '2002', value: '2002' },
        { name: '2003', value: '2003' },
        { name: '2004', value: '2004' },
        { name: '2005', value: '2005' },
        { name: '2006', value: '2006' },
        { name: '2007', value: '2007' },
        { name: '2008', value: '2008' },
        { name: '2009', value: '2009' },
        { name: '2010', value: '2010' },
        { name: '2011', value: '2011' },
        { name: '2012', value: '2012' },
        { name: '2013', value: '2013' },
        { name: '2014', value: '2014' },
        { name: '2015', value: '2015' },
        { name: '2016', value: '2016' },
        { name: '2017', value: '2017' },
        { name: '2018', value: '2018' },
        { name: '2019', value: '2019' },
        { name: '2020', value: '2020' },
        { name: '2021', value: '2021' },
        { name: '2022', value: '2022' },
        { name: '2023', value: '2023' },
        { name: '2024', value: '2024' },
        { name: '2025', value: '2025' }
    ];
    yearsVideos = [
        { name: 'All years', value: '' },
        { name: '2007', value: '2007' },
        { name: '2008', value: '2008' },
        { name: '2009', value: '2009' },
        { name: '2010', value: '2010' },
        { name: '2011', value: '2011' },
        { name: '2012', value: '2012' },
        { name: '2013', value: '2013' },
        { name: '2014', value: '2014' },
        { name: '2015', value: '2015' },
        { name: '2016', value: '2016' },
        { name: '2017', value: '2017' },
        { name: '2018', value: '2018' },
        { name: '2019', value: '2019' },
        { name: '2020', value: '2020' },
        { name: '2021', value: '2021' },
        { name: '2022', value: '2022' },
        { name: '2023', value: '2023' },
        { name: '2024', value: '2024' },
        { name: '2025', value: '2025' }
    ];

    types = [
        { name: 'All types', value: '' },
        { name: 'Lecture', value: 'lecture' },
        { name: 'Tutorial', value: 'tutorial' },
        { name: 'Keynote', value: 'keynote' },
        { name: 'Demonstration', value: 'demonstration' },
        { name: 'Opening', value: 'opening' },
        { name: 'Interview', value: 'interview' },
        { name: 'Debate', value: 'debate' },
        { name: 'Panel', value: 'panel' },
        { name: 'Summary', value: 'summary' },
    ];

    languageOptions = [
        { name: 'All languages', value: '' },
        { name: 'Slovenian', value: 'sl' },
        { name: 'English', value: 'en' },
    ];

    public toggleOrder(order: SortOrder): void {
        this.router.navigate([], {
            queryParams: { order: order === SortOrder.Descending ? SortOrder.Ascending : SortOrder.Descending },
            queryParamsHandling: 'merge'
        });
    }

    public toggleView(): void {
        this.viewService.toggleView();
    }
}
